import { useSigma, useRegisterEvents } from "@react-sigma/core"; //"react-sigma-v2";
import { FC, useEffect } from "react";
import { Settings } from "sigma/settings";
import { PlainObject } from "sigma/types";
import { drawHover, drawHoverEdge } from "./canvas-utils";
import useDebounce from "./use-debounce";

const NODE_FADE_COLOR = "#bbb";
const EDGE_FADE_COLOR = "#eee";

const GraphSettingsController: FC<{
  children?: React.ReactNode;
  hoveredNode: string | null;
  hoveredEdge: string | null;
}> = ({ children, hoveredNode, hoveredEdge }) => {
  const sigma = useSigma();
  const graph = sigma.getGraph();
  const registerEvents = useRegisterEvents();

  // Here we debounce the value to avoid having too much highlights refresh when
  // moving the mouse over the graph:
  const debouncedHoveredNode = useDebounce(hoveredNode, 40);
  const debouncedHoveredEdge = useDebounce(hoveredEdge, 40);

  /**
   * Initialize here settings that require to know the graph and/or the sigma
   * instance:
   */
  useEffect(() => {
    sigma.setSetting("hoverRenderer", (context, data, settings) => {
      drawHover(
        context,
        { ...sigma.getNodeDisplayData(data.key), ...data },
        settings
      );
    });
  }, [sigma, graph]);

  /**
   * Update node and edge reducers when a node is hovered, to highlight its
   * neighborhood:
   */
  useEffect(() => {
    if (debouncedHoveredEdge) {
      // Edge hover: highlight only the hovered edge and its extremities
      // const edgeData = graph.getEdgeAttributes(debouncedHoveredEdge);
      if (!graph.hasEdge(debouncedHoveredEdge)) return;
      const [source, target] = graph.extremities(debouncedHoveredEdge);

      const sourceColor = graph.getNodeAttribute(source, "color");
      const targetColor = graph.getNodeAttribute(target, "color");
      // const sourceLabel = graph.getNodeAttribute(source, "label");
      // const targetLabel = graph.getNodeAttribute(target, "label");

      // const sourceData = graph.getNodeAttributes(source);
      // const targetData = graph.getNodeAttributes(target);
      // const x = (sourceData.x + targetData.x) / 2;
      // const y = (sourceData.y + targetData.y) / 2;

      // console.log("Edge data:", { sourceLabel, targetLabel, x, y });

      sigma.setSetting("nodeReducer", (node, data) => {
        if (node === source) {
          return { ...data, zIndex: 1, color: sourceColor };
        } else if (node === target) {
          return { ...data, zIndex: 1, color: targetColor };
        } else {
          return {
            ...data,
            zIndex: 0,
            label: "",
            color: NODE_FADE_COLOR,
            image: null,
            highlighted: false,
          };
        }
      });
      sigma.setSetting("edgeReducer", (edge, data) =>
        edge === debouncedHoveredEdge
          ? { ...data, color: "#0099ff", size: 4 } // Highlight color and size
          : { ...data, color: EDGE_FADE_COLOR, hidden: true }
      );

      // sigma.refresh();
      // console.log("Called sigma.refresh()");

      // // Add tooltip after refresh
      // requestAnimationFrame(() => {
      //   console.log("Inside requestAnimationFrame");
      //   const canvas = sigma.getContainer().querySelector("canvas");
      //   if (canvas) {
      //     console.log("Canvas found");
      //     const context = canvas.getContext("2d");
      //     if (context) {
      //       console.log("About to call drawHoverEdge");
      //       drawHoverEdge(
      //         context,
      //         {
      //           x,
      //           y,
      //           sourceLabel,
      //           targetLabel,
      //         },
      //         sigma.getSettings()
      //       );
      //     }
      //   }
      // });
    } else if (debouncedHoveredNode) {
      const hoveredColor: string =
        sigma.getNodeDisplayData(debouncedHoveredNode)!.color;

      sigma.setSetting("nodeReducer", (node, data) =>
        node === debouncedHoveredNode ||
        graph.hasEdge(node, debouncedHoveredNode) ||
        graph.hasEdge(debouncedHoveredNode, node)
          ? { ...data, zIndex: 1 }
          : {
              ...data,
              zIndex: 0,
              label: "",
              color: NODE_FADE_COLOR,
              image: null,
              highlighted: false,
            }
      );
      sigma.setSetting("edgeReducer", (edge, data) =>
        graph.hasExtremity(edge, debouncedHoveredNode)
          ? { ...data, color: hoveredColor, size: 4 }
          : { ...data, color: EDGE_FADE_COLOR, hidden: true }
      );
    } else {
      sigma.setSetting("nodeReducer", null);
      sigma.setSetting("edgeReducer", null);
    }
  }, [sigma, graph, debouncedHoveredNode, debouncedHoveredEdge]);

  useEffect(() => {
    registerEvents({
      afterRender: () => {
        const hoverCanvas = sigma
          .getContainer()
          .querySelector(".sigma-hovers") as HTMLCanvasElement;
        if (!hoverCanvas) return;
        const ctx = hoverCanvas.getContext("2d");
        if (!ctx) return;

        if (!debouncedHoveredEdge) return;
        if (!graph.hasEdge(debouncedHoveredEdge)) return;

        const [source, target] = graph.extremities(debouncedHoveredEdge);
        const sourceLabel = graph.getNodeAttribute(source, "label");
        const targetLabel = graph.getNodeAttribute(target, "label");

        const sourceData = sigma.getNodeDisplayData(source);
        const targetData = sigma.getNodeDisplayData(target);

        console.log(sourceData, targetData);

        if (!sourceData || !targetData) return;

        const x = (sourceData.x + targetData.x) / 2;
        const y = (sourceData.y + targetData.y) / 2;

        const screenCoords = sigma.framedGraphToViewport({ x, y });
        console.log("Screen coordinates:", screenCoords);
        console.log("Hover edge position:", { x, y });
        const settings = sigma.getSettings();

        drawHoverEdge(
          ctx,
          { x: screenCoords.x, y: screenCoords.y, sourceLabel, targetLabel },
          settings
        );
      },
    });
  }, [sigma, graph, debouncedHoveredEdge, registerEvents]);

  return <>{children}</>;
};

export default GraphSettingsController;
